import React, { useCallback, useEffect, useRef, useState } from "react";
import "./LeftSidebar.scss";
import { IoChevronDown, IoChevronUp, IoSearchOutline, IoArrowBackCircleOutline, IoArrowForwardCircleOutline } from "react-icons/io5";
import { Category, Filter, Song } from "../../../@core/models";
import RestClient from "../../../@core/restClient";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";

type CategoriesState = [
  categories: Category[],
  setCategories: (categories: Category[]) => void
];

type SongsState = [songs: Song[], setSongs: (songs: Song[]) => void];

type FilterState = [
  filter: Filter,
  setFilter: (arg: Filter | ((current: Filter) => Filter)) => void
];

const LeftSidebar = ({
  songListModified,
  closeSidebar,
  useCategories,
  onSongsNumber,
  onSongsFetched,
  newSongId,
  categoryList,
  songDeleted,
  isLibrary,
  librarySongs,
  isLoading
}: any) => {
  const { songbookId, songId } = useParams();
  const [searchParams] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const activeItemRef = useRef<HTMLLIElement>(null);
  //const [categories, setCategories]: CategoriesState = useState<Category[]>([]);
  const [songs, setSongs]: SongsState = useState<Song[]>([]);
  const [filter, setFilter]: FilterState = useState<Filter>({
    category: "",
    query: ""
  });
  const [filteredSongs, setFilteredSongs] = useState<Song[]>([]);
  const [sbPage, setSbPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [useBackendFilter, setUseBackendFilter] = useState(false);
  const [searchInText, setSearchInText] = useState(false);
  const searchInTextRef = useRef(searchInText);
  const [sort, setSort] = useState("");
  const [sortOption, setSortOption] = useState("Сортувати");
  const [sortIsOpen, setSortIsOpen] = useState(false);
  const [category, setCategory] = useState("Всі категорії");
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(true);
  const [options, setOptions] = useState("hideOptions");
  const navigate = useNavigate();

  searchInTextRef.current = searchInText;

  const deferredSetQueryHandler = useCallback(
    debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      if (isLibrary) {
        const params = new URLSearchParams(window.location.search);
        const value = event.target.value;
        if (value) {
          params.set("query", value);
          if (searchInTextRef.current) params.set("searchQueryRules", "text");
          else params.delete("searchQueryRules");
        } else {
          params.delete("query");
          params.delete("searchQueryRules");
        }
        params.delete("page");
        navigate({ search: params.toString() });
      } else {
        setFilter((current: Filter) => ({
          ...current,
          query: event.target.value
        }));
      }
    }, 500),
    []
  );

  const getSongList = async () => {
    if (!songbookId) return;
    try {
      const response = await RestClient.getSongList(songbookId, undefined, undefined, 1, 250);
      const sorted = [...response].sort((a, b) => Number(b.id) - Number(a.id));
      setSongs(sorted);
      setFilteredSongs(sorted);
      const atLimit = response.length === 250;
      setHasMore(atLimit);
      setUseBackendFilter(atLimit);
      onSongsNumber(response.length);
      onSongsFetched?.(response.length);
      if (response.length !== 0) {
        if (songListModified && !songDeleted) {
          navigate(`/songbook/${songbookId}/song/${newSongId}`);
        } else if (songDeleted) {
          const deletedId = Number(songId);
          const above = sorted.filter((s) => s.id !== null && s.id !== undefined && s.id > deletedId);
          const target = above.length ? above[above.length - 1] : sorted[0];
          navigate(`/songbook/${songbookId}/song/${target.id}`);
        } else if (!songId && searchParams.get("intro") !== "1") {
          navigate(`/songbook/${songbookId}/song/${sorted[0].id}`);
        } else if (response.some((song) => song.id === Number(songId))) {
          navigate(`/songbook/${songbookId}/song/${songId}`);
        }
      } else {
        navigate(`/songbook/${songbookId}`);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const loadMoreSongs = async () => {
    if (!songbookId) return;
    const nextPage = sbPage + 1;
    setIsLoadingMore(true);
    try {
      const response = await RestClient.getSongList(songbookId, undefined, undefined, nextPage, 250);
      const sorted = [...response].sort((a, b) => Number(b.id) - Number(a.id));
      setSongs([...songs, ...sorted]);
      setFilteredSongs([...filteredSongs, ...sorted]);
      setHasMore(response.length === 250);
      setSbPage(nextPage);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const goToSongPage = async (p: number) => {
    if (!songbookId) return;
    setIsLoadingMore(true);
    try {
      const response = await RestClient.getSongList(songbookId, undefined, undefined, p, 250);
      const sorted = [...response].sort((a, b) => Number(b.id) - Number(a.id));
      setSongs(sorted);
      setFilteredSongs(sorted);
      setHasMore(response.length === 250);
      setSbPage(p);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // FILTER SONGS BY SEARCH AND CATEGORY

  const normalize = (str: any) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/[.,\-/#!$%^&*;:{}=_`~()?'"@]/g, "") // Remove symbols
      .replace(/\s+/g, " ") // Normalize spaces
      .trim();
  };
  const filterSongList = () => {
    const normalizedQuery = normalize(filter.query);

    const filtered = songs.filter((el) => {
      const titleMatch = normalize(el.title).includes(normalizedQuery);
      const subTitleMatch = normalize(el.subTitle).includes(normalizedQuery);
      const textMatch = searchInText ? normalize(el.text).includes(normalizedQuery) : false;
      const queryMatch = titleMatch || subTitleMatch || textMatch;
      const categoryMatch = el.categories
        ?.map((item) => item.id)
        .includes(filter.category);

      return filter.category && !filter.query
        ? categoryMatch
        : filter.query && !filter.category
          ? queryMatch
          : filter.category && filter.query
            ? categoryMatch && queryMatch
            : true;
    });

    setFilteredSongs(filtered);
  };

  //CATEGORIES BOX
  const openCategories = (e: any) => {
    e.stopPropagation();
    setCategoriesOpen(!categoriesOpen);
    if (sortIsOpen) {
      setSortIsOpen(false);
    }
  };

  const chooseCategory = (id: any, name: any, e: any) => {
    e.stopPropagation();
    setCategoriesOpen(false);
    setCategory(name);
    if (isLibrary) {
      const params = new URLSearchParams(window.location.search);
      if (id) params.set("category", name);
      else params.delete("category");
      params.delete("page");
      navigate({ search: params.toString() });
    } else {
      setFilter({ ...filter, category: id });
    }
  };

  //SORTING SONG LIST
  const openSort = (e: any) => {
    e.stopPropagation();
    setSortIsOpen(!sortIsOpen);
    if (categoriesOpen) {
      setCategoriesOpen(false);
    }
  };

  useEffect(() => {
    if (!sortIsOpen && !categoriesOpen) return;
    const handler = () => { setSortIsOpen(false); setCategoriesOpen(false); };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [sortIsOpen, categoriesOpen]);

  const sorting = (option: any, e: any) => {
    e.stopPropagation();
    setSortIsOpen(false);
    if (option === "az") {
      setSort(option);
      setSortOption("А - Я");
    }

    if (option === "za") {
      setSort(option);
      setSortOption("Я - А");
    }
    if (option === "newOld") {
      setSort(option);
      setSortOption("Нові - Старі");
    }
    if (option === "") {
      setSort(option);
      setSortOption("Не сортувати");
    }
    if (option === "oldNew") {
      setSort(option);
      setSortOption("Старі - Нові");
    }
  };
  useEffect(() => {
    if (sort === "az") {
      const sorted = [...filteredSongs].sort((a, b) =>
        a.title.localeCompare(b.title)
      );
      setFilteredSongs(sorted);
    }

    if (sort === "za") {
      const sorted = [...filteredSongs].sort((a, b) =>
        b.title.localeCompare(a.title)
      );
      setFilteredSongs(sorted);
    }
    if (sort === "oldNew") {
      const sorted = [...filteredSongs].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      setFilteredSongs(sorted);
    }
    if (sort === "newOld" || sort === "") {
      const sorted = [...filteredSongs].sort(
        (a, b) => Number(b.id) - Number(a.id)
      );
      setFilteredSongs(sorted);
    }
  }, [sort]);

  useEffect(() => {
    if (isLibrary) return;
    (async () => { await getSongList(); })();
  }, [songbookId]);

  useEffect(() => {
    if (isLibrary || !songListModified) return;
    (async () => { await getSongList(); })();
  }, [songListModified]);

  useEffect(() => {
    if (!isLibrary) return;
    setSongs(librarySongs || []);
    setFilteredSongs(librarySongs || []);
  }, [librarySongs]);

  useEffect(() => {
    if (!isLibrary) return;
    const catName = searchParams.get("category");
    if (catName) setCategory(catName);
    if (searchInputRef.current) searchInputRef.current.value = searchParams.get("query") || "";
  }, [isLibrary]);

  useEffect(() => {
    if (!songId || !activeItemRef.current) return;
    activeItemRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [songId, filteredSongs]);

  useEffect(() => {
    if (isLibrary) return;
    if (useBackendFilter && (filter.query || filter.category)) {
      (async () => {
        try {
          const response = await RestClient.getSongList(
            songbookId!,
            filter.category ? String(filter.category) : undefined,
            filter.query || undefined,
            1,
            250,
            searchInText ? "text" : undefined
          );
          setFilteredSongs([...response].sort((a, b) => Number(b.id) - Number(a.id)));
        } catch (e) {
          console.log(e);
        }
      })();
    } else {
      filterSongList();
    }
  }, [filter, songs, useBackendFilter, searchInText]);


  const toggleSearchInText = () => {
    const next = !searchInText;
    setSearchInText(next);
    if (isLibrary) {
      const params = new URLSearchParams(window.location.search);
      const hasQuery = !!params.get("query");
      if (!hasQuery) return;
      if (next) params.set("searchQueryRules", "text");
      else params.delete("searchQueryRules");
      navigate({ search: params.toString() });
    }
  };

  // STOP PROPAGATION ON INPUT TO FIND A SONG
  const notHide = (e: any) => {
    e.stopPropagation();
  };

  // ClOSE SORT OR CATIGORY BOXES

  const toggleCategories = () => {
    setCategoriesOpen(!categoriesOpen);
    if (sortIsOpen) {
      setSortIsOpen(false);
    } else {
      return;
    }
  };
  const toggleSort = () => {
    setSortIsOpen(!sortIsOpen);
    if (categoriesOpen) {
      setCategoriesOpen(false);
    }
  };

  // HANDLE SHOW SUBTITLE BUTTON

  useEffect(() => {
    const subtitle = JSON.parse(localStorage.getItem("subtitle") || "true");
    setShowSubtitle(subtitle);
  }, []);

  const switchShowSubtitle = () => {
    if (showSubtitle) {
      setShowSubtitle(false);
      localStorage.setItem("subtitle", JSON.stringify(false));
    } else {
      setShowSubtitle(true);
      localStorage.setItem("subtitle", JSON.stringify(true));
    }
  };

  // SHOW OPTIONS

  const toggleOptions = () => {
    if (options === "showOptions") {
      setOptions("hideOptions");
    } else {
      setOptions("showOptions");
    }
  };

  return (
    <div className="left-sidebar" onClick={(e) => notHide(e)}>
      <div className="search-field">
        <input
          ref={searchInputRef}
          type="text"
          className="search-song"
          placeholder={isLibrary ? "Знайти пісню ..." : "Шукати за назвою ..."}
          onChange={deferredSetQueryHandler}
          onClick={(e) => notHide(e)}
        />
        <IoSearchOutline className="search-icon" />
      </div>
      <label className="search-in-text">
        <input
          type="checkbox"
          className="search-in-text-input"
          checked={searchInText}
          onChange={toggleSearchInText}
        />
        <span className="search-in-text-box" />
        Шукати у тексті
      </label>
      <div className="optionFilterBtn" onClick={toggleOptions}>
        <div>Фільтри</div>
        {options === "showOptions" ? (
          <IoChevronUp className="icon" />
        ) : (
          <IoChevronDown className="icon" />
        )}
      </div>
      <div className={options}>
        {useCategories ? (
          <div className="topic">
            <div className="topic-input-box">
              <input
                readOnly
                type="text"
                className="topic-field"
                value={category}
                onClick={(e) => openCategories(e)}
              />
              {categoriesOpen ? (
                <IoChevronUp
                  className="chevron-icon"
                  onClick={toggleCategories}
                />
              ) : (
                <IoChevronDown
                  className="chevron-icon"
                  onClick={toggleCategories}
                />
              )}
            </div>
            <div className={`topic-options${categoriesOpen ? " topic-options--open" : ""}`}>
              <p
                className="topic-options-item topic-options-item--all"
                onClick={(e) => chooseCategory(null, "Всі категорії", e)}
              >
                Всі категорії
              </p>
              {[...(categoryList ?? [])].sort((a: Category, b: Category) => a.name.localeCompare(b.name, "uk")).map((category: Category) => (
                <p
                  key={category.id}
                  className="topic-options-item"
                  onClick={(e) =>
                    chooseCategory(category.id, category.name, e)
                  }
                >
                  {category.name}
                </p>
              ))}
            </div>
          </div>
        ) : (
          ""
        )}
        <div className="topic">
          <div className="topic-input-box">
            <input
              readOnly
              type="text"
              className="topic-field"
              value={sortOption}
              onClick={(e) => openSort(e)}
            />
            {sortIsOpen ? (
              <IoChevronUp className="chevron-icon" onClick={toggleSort} />
            ) : (
              <IoChevronDown className="chevron-icon" onClick={toggleSort} />
            )}
          </div>
          <div className={`topic-options${sortIsOpen ? " topic-options--open" : ""}`}>
            <p className="topic-options-item" onClick={(e) => sorting("", e)}>
              Не сортувати
            </p>
            <p
              className="topic-options-item"
              onClick={(e) => sorting("az", e)}
            >
              А - Я
            </p>
            <p
              className="topic-options-item"
              onClick={(e) => sorting("za", e)}
            >
              Я - А
            </p>
            <p
              className="topic-options-item"
              onClick={(e) => sorting("newOld", e)}
            >
              Останні - перші
            </p>
            <p
              className="topic-options-item"
              onClick={(e) => sorting("oldNew", e)}
            >
              Перші - останні
            </p>
          </div>
        </div>
        <div className="switchSubtitle-box">
          <p>Підзаголовки </p>

          <div
            className={`switchSubtitle ${showSubtitle ? "onSubtitle" : ""}`}
            onClick={switchShowSubtitle}
          >
            <div className="sliderSubtitle"></div>
          </div>
          <p> {showSubtitle ? "On" : "Off"}</p>
        </div>
      </div>
      <ul className="contents">
        {isLoading && !filteredSongs?.length && (
          <li className="sidebar-spinner-item">
            <span className="sidebar-spinner" />
          </li>
        )}
        {filteredSongs?.map((song: Song) => (
          <li
            key={song.id}
            ref={Number(songId) === song.id ? activeItemRef : null}
            className={Number(songId) === song.id ? "is-active" : ""}
          >
            <Link
              to={isLibrary ? `/library/song/${song.id}` : `/songbook/${songbookId}/song/${song.id}`}
              onClick={closeSidebar}
            >
              {song.title}
              {showSubtitle ? (
                <span className="songSubtitle">{song.subTitle}</span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
      <p className="songsnumber">Пісень на сторінці - {songs.length}</p>
      {!isLibrary && (
        <div className="library-pagination">
          <button
            className="library-pagination-load-more"
            onClick={loadMoreSongs}
            disabled={!hasMore || isLoadingMore}
          >
            {isLoadingMore ? <span className="library-pagination-spinner" /> : "Показати ще"}
          </button>
          <div className="library-pagination-nav">
            {sbPage > 1 && !isLoadingMore ? (
              <a
                className="library-pagination-arrow"
                href={`/songbook/${songbookId}?page=${sbPage - 1}`}
                onClick={(e) => { e.preventDefault(); goToSongPage(sbPage - 1); }}
              >
                <IoArrowBackCircleOutline />
              </a>
            ) : (
              <span className={`library-pagination-arrow${sbPage <= 1 || isLoadingMore ? " disabled" : ""}`}>
                <IoArrowBackCircleOutline />
              </span>
            )}
            <span className="library-pagination-page">{sbPage}</span>
            {hasMore && !isLoadingMore ? (
              <a
                className="library-pagination-arrow"
                href={`/songbook/${songbookId}?page=${sbPage + 1}`}
                onClick={(e) => { e.preventDefault(); goToSongPage(sbPage + 1); }}
              >
                <IoArrowForwardCircleOutline />
              </a>
            ) : (
              <span className={`library-pagination-arrow${!hasMore || isLoadingMore ? " disabled" : ""}`}>
                <IoArrowForwardCircleOutline />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeftSidebar;
